import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Video, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
    const { login, loginWithEmail, signUpWithEmail, user } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: ''
    });

    // Redirect if already logged in
    React.useEffect(() => {
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const handleGoogleLogin = async () => {
        if (isSignUp && !agreed) {
            toast.error('Please accept the Privacy Policy and Terms to continue');
            return;
        }
        setIsLoading(true);
        try {
            await login('google');
        } catch (error) {
            toast.error('Failed to login with Google');
            setIsLoading(false);
        }
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.password) {
            toast.error('Please fill in all fields');
            return;
        }
        if (isSignUp && !agreed) {
            toast.error('Please accept the Privacy Policy and Terms to continue');
            return;
        }

        setIsLoading(true);
        try {
            if (isSignUp) {
                await signUpWithEmail(formData.email, formData.password, formData.name);
                toast.success('Account created! Check your email to verify.');
            } else {
                await loginWithEmail(formData.email, formData.password);
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error(error.message || 'Authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-void flex items-center justify-center px-6 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-neon-magenta/10 rounded-full blur-[150px]" />
            </div>

            <motion.div
                className="relative z-10 w-full max-w-md"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-3 mb-6">
                        <img src="/logo.png" alt="RevealIQ Meet" className="h-24 object-contain" />
                    </div>
                    <h1 className="font-heading font-bold text-3xl text-white mb-2">
                        {isSignUp ? 'Create Account' : 'Welcome Back'}
                    </h1>
                    <p className="text-grey-400 font-body">
                        {isSignUp ? 'Sign up to start your meetings' : 'Sign in to continue to your meetings'}
                    </p>
                </div>

                {/* Login Card */}
                <div className="glass-card p-8">
                    {/* Google Login Button */}
                    <Button
                        onClick={handleGoogleLogin}
                        disabled={isLoading}
                        className="w-full bg-white hover:bg-grey-100 text-black font-semibold py-6 rounded-xl flex items-center justify-center gap-3 mb-6 transition-all duration-300"
                        data-testid="google-login-btn"
                    >
                        {isLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                Continue with Google
                            </>
                        )}
                    </Button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-grey-400 text-sm font-body">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    {/* Email Form */}
                    <form onSubmit={handleEmailAuth} className="space-y-4">
                        {isSignUp && (
                            <div>
                                <label className="text-sm text-grey-400 mb-1 block">Full Name</label>
                                <Input
                                    type="text"
                                    placeholder="John Doe"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="input-neon"
                                    data-testid="name-input"
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-sm text-grey-400 mb-1 block">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
                                <Input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="input-neon pl-11"
                                    data-testid="email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-grey-400 mb-1 block">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="input-neon pl-11"
                                    data-testid="password-input"
                                />
                            </div>
                        </div>

                        {isSignUp && (
                            <label className="flex items-start gap-2 text-xs text-grey-400 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreed}
                                    onChange={(e) => setAgreed(e.target.checked)}
                                    className="mt-0.5 accent-neon-cyan"
                                    data-testid="consent-checkbox"
                                />
                                <span>
                                    I agree to the{' '}
                                    <Link to="/terms" className="text-neon-cyan hover:underline">Terms</Link> and{' '}
                                    <Link to="/privacy" className="text-neon-cyan hover:underline">Privacy Policy</Link>,
                                    and consent to processing of my meeting data.
                                </span>
                            </label>
                        )}

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full btn-neon py-6"
                            data-testid="email-login-btn"
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    {isSignUp ? 'Create Account' : 'Sign In'}
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Toggle Sign Up / Sign In */}
                    <p className="text-center text-grey-400 text-sm mt-6">
                        {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                        <button
                            onClick={() => setIsSignUp(!isSignUp)}
                            className="text-neon-cyan hover:underline font-semibold"
                            data-testid="toggle-auth-mode"
                        >
                            {isSignUp ? 'Sign In' : 'Sign Up'}
                        </button>
                    </p>
                </div>

                {/* Legal */}
                <p className="text-center text-grey-500 text-xs mt-6">
                    <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                    <span className="mx-2">·</span>
                    <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                </p>
            </motion.div>
        </div>
    );
};

export default LoginPage;
